// components/MicroserviceDetails.jsx
import React, { useState } from 'react';
import {
  Container,
  Header,
  Tabs,
  SpaceBetween,
  ColumnLayout,
  Box,
  Table,
  Link,
  ExpandableSection,
  StatusIndicator,
  Alert
} from '@cloudscape-design/components';
import ReactECharts from 'echarts-for-react';

const MicroserviceDetails = ({ microservice, onBack }) => {
  const [activeTab, setActiveTab] = useState('overview');

  // Example file structure data
  const fileStructure = [
    {
      type: 'directory',
      path: 'src/main/java/com/company/servicename/model',
      files: [
        { name: 'Entity.java', type: 'file', status: 'generated' }
      ]
    },
    {
      type: 'directory',
      path: 'src/main/java/com/company/servicename/repository',
      files: [
        { name: 'EntityRepository.java', type: 'file', status: 'generated' }
      ]
    },
    {
      type: 'directory',
      path: 'src/main/java/com/company/servicename/service',
      files: [
        { name: 'EntityService.java', type: 'file', status: 'generated' },
        { name: 'impl/EntityServiceImpl.java', type: 'file', status: 'generated' }
      ]
    },
    {
      type: 'directory',
      path: 'src/main/java/com/company/servicename/controller',
      files: [
        { name: 'EntityController.java', type: 'file', status: 'generated' }
      ]
    },
    {
      type: 'directory',
      path: 'src/main/java/com/company/servicename/config',
      files: [
        { name: 'ApplicationConfig.java', type: 'file', status: 'generated' }
      ]
    },
    {
      type: 'directory',
      path: 'src/main/resources',
      files: [
        { name: 'application.yml', type: 'file', status: 'generated' }
      ]
    },
    {
      type: 'directory',
      path: 'src/test/java/com/company/servicename',
      files: [
        { name: 'EntityControllerTest.java', type: 'file', status: 'generated' }
      ]
    }
  ];

  const renderFileStructure = () => {
    const treeData = {
      name: 'project-root',
      children: [
        {
          name: 'src',
          symbol: 'folder',
          children: [
            {
              name: 'main',
              symbol: 'folder',
              children: [
                {
                  name: 'java',
                  symbol: 'folder',
                  children: [
                    {
                      name: 'com.company.servicename',
                      symbol: 'folder',
                      children: [
                        {
                          name: 'model',
                          symbol: 'folder',
                          children: [
                            { 
                              name: 'Entity.java',
                              symbol: 'java-file'
                            }
                          ]
                        },
                        {
                          name: 'repository',
                          symbol: 'folder',
                          children: [
                            { 
                              name: 'EntityRepository.java',
                              symbol: 'java-file'
                            }
                          ]
                        },
                        {
                          name: 'service',
                          symbol: 'folder',
                          children: [
                            { 
                              name: 'EntityService.java',
                              symbol: 'java-file'
                            },
                            {
                              name: 'impl',
                              symbol: 'folder',
                              children: [
                                { 
                                  name: 'EntityServiceImpl.java',
                                  symbol: 'java-file'
                                }
                              ]
                            }
                          ]
                        },
                        {
                          name: 'controller',
                          symbol: 'folder',
                          children: [
                            { 
                              name: 'EntityController.java',
                              symbol: 'java-file'
                            }
                          ]
                        },
                        {
                          name: 'config',
                          symbol: 'folder',
                          children: [
                            { 
                              name: 'ApplicationConfig.java',
                              symbol: 'java-file'
                            }
                          ]
                        }
                      ]
                    }
                  ]
                },
                {
                  name: 'resources',
                  symbol: 'folder',
                  children: [
                    { 
                      name: 'application.yml',
                      symbol: 'yml-file'
                    }
                  ]
                }
              ]
            },
            {
              name: 'test',
              symbol: 'folder',
              children: [
                {
                  name: 'java',
                  symbol: 'folder',
                  children: [
                    {
                      name: 'com.company.servicename',
                      symbol: 'folder',
                      children: [
                        {
                          name: 'controller',
                          symbol: 'folder',
                          children: [
                            { 
                              name: 'EntityControllerTest.java',
                              symbol: 'java-file'
                            }
                          ]
                        }
                      ]
                    }
                  ]
                }
              ]
            }
          ]
        },
        { 
          name: 'pom.xml',
          symbol: 'xml-file'
        },
        { 
          name: 'Dockerfile',
          symbol: 'file'
        },
        { 
          name: 'README.md',
          symbol: 'file'
        }
      ]
    };
  
    const option = {
      tooltip: {
        trigger: 'item',
        triggerOn: 'mousemove'
      },
      series: [
        {
          type: 'tree',
          data: [treeData],
          top: '5%',
          left: '10%',
          bottom: '5%',
          right: '20%',
          symbolSize: 12,
          symbol: function (value, params) {
            const symbolType = params.data.symbol || 'file';
            switch (symbolType) {
              case 'folder':
                return 'path://M 0,3 L 14,3 L 14,13 L 0,13 Z M 0,0 L 8,0 L 8,2 L 0,2 Z';
              case 'java-file':
                return 'path://M 12,0 L 12,2 L 0,2 L 0,14 L 14,14 L 14,0 Z M 3,4 L 11,4 L 11,6 L 3,6 Z';
              case 'xml-file':
                return 'path://M 12,0 L 12,2 L 0,2 L 0,14 L 14,14 L 14,0 Z M 3,7 L 11,7 L 11,9 L 3,9 Z';
              case 'yml-file':
                return 'path://M 12,0 L 12,2 L 0,2 L 0,14 L 14,14 L 14,0 Z M 3,4 L 11,4 L 11,6 L 3,6 Z';
              default:
                return 'path://M 12,0 L 12,2 L 0,2 L 0,14 L 14,14 L 14,0 Z';
            }
          },
          itemStyle: {
            color: function(params) {
              const symbolType = params.data.symbol || 'file';
              switch (symbolType) {
                case 'folder':
                  return '#42a887';
                case 'java-file':
                  return '#007acc';
                case 'xml-file':
                  return '#f29111';
                case 'yml-file':
                  return '#724bb7';
                default:
                  return '#8c8c8c';
              }
            }
          },
          label: {
            position: 'right',
            verticalAlign: 'middle',
            align: 'left',
            fontSize: 14
          },
          leaves: {
            label: {
              position: 'right',
              verticalAlign: 'middle',
              align: 'left'
            }
          },
          emphasis: {
            focus: 'descendant'
          },
          expandAndCollapse: true,
          animationDuration: 550,
          animationDurationUpdate: 750,
          initialTreeDepth: -1,
          lineStyle: {
            width: 1,
            color: '#ccc'
          }
        }
      ]
    };

    const onChartClick = (params) => {
      if (params.data.name.includes('.')) {
        // Handle file click
        console.log('Clicked file:', params.data.name);
        // You could show file contents in a modal or side panel
      }
    };
  
    return (
      <Box padding="l">
        <SpaceBetween size="l">
          <Alert type="info">
            Click on any file to view its contents. Use mouse wheel to zoom and drag to pan.
          </Alert>
          <ReactECharts
            option={option}
            style={{ height: '800px' }}
            opts={{ renderer: 'canvas' }}
            onEvents={{
              click: onChartClick
            }}
          />
        </SpaceBetween>
      </Box>
    );
  };

  return (
    <Container
      header={
        <Header
          variant="h2"
          description={`Generated from ${microservice.domain}`}
        >
          {microservice.name}
        </Header>
      }
    >
      <Tabs
        activeTabId={activeTab}
        onChange={({ detail }) => setActiveTab(detail.activeTabId)}
        tabs={[
          {
            label: "Overview",
            id: "overview",
            content: (
              <SpaceBetween size="l">
                <ColumnLayout columns={2}>
                  <Box>
                    <SpaceBetween size="xs">
                      <Box variant="awsui-key-label">Repository</Box>
                      <Link href={`https://${microservice.repository}`} external>
                        {microservice.repository}
                      </Link>
                    </SpaceBetween>
                  </Box>
                  <Box>
                    <SpaceBetween size="xs">
                      <Box variant="awsui-key-label">CI/CD Pipeline</Box>
                      <Link href={`https://${microservice.pipeline}`} external>
                        {microservice.pipeline}
                      </Link>
                    </SpaceBetween>
                  </Box>
                </ColumnLayout>
                <ColumnLayout columns={2}>
                  <Box>
                    <SpaceBetween size="xs">
                      <Box variant="awsui-key-label">Generated At</Box>
                      <Box>{new Date(microservice.generatedAt).toLocaleString()}</Box>
                    </SpaceBetween>
                  </Box>
                  <Box>
                    <SpaceBetween size="xs">
                      <Box variant="awsui-key-label">Status</Box>
                      <StatusIndicator type={
                        microservice.status === 'GENERATED' ? 'success' :
                        microservice.status === 'IN_PROGRESS' ? 'in-progress' :
                        'pending'
                      }>
                        {microservice.status}
                      </StatusIndicator>
                    </SpaceBetween>
                  </Box>
                </ColumnLayout>
              </SpaceBetween>
            )
          },
          {
            label: "File Structure",
            id: "structure",
            content: renderFileStructure()
          },
          {
            label: "API Documentation",
            id: "api",
            content: (
              <Table
                columnDefinitions={[
                  {
                    id: 'endpoint',
                    header: 'Endpoint',
                    cell: item => item.endpoint
                  },
                  {
                    id: 'method',
                    header: 'Method',
                    cell: item => (
                      <StatusIndicator type={
                        item.method === 'GET' ? 'success' :
                        item.method === 'POST' ? 'info' :
                        item.method === 'PUT' ? 'warning' :
                        'error'
                      }>
                        {item.method}
                      </StatusIndicator>
                    )
                  },
                  {
                    id: 'description',
                    header: 'Description',
                    cell: item => item.description
                  }
                ]}
                items={[
                  {
                    endpoint: '/api/v1/entities',
                    method: 'GET',
                    description: 'List all entities'
                  },
                  {
                    endpoint: '/api/v1/entities',
                    method: 'POST',
                    description: 'Create new entity'
                  },
                  // Add more endpoints
                ]}
                variant="container"
              />
            )
          },
          {
            label: "Configuration",
            id: "config",
            content: (
              <SpaceBetween size="l">
                <ExpandableSection header="application.yml">
                  <Box variant="pre">
                    {`spring:
  application:
    name: ${microservice.name}
  datasource:
    url: jdbc:postgresql://localhost:5432/${microservice.name}
    username: ${microservice.name}
    password: password
server:
  port: 8080`}
                  </Box>
                </ExpandableSection>
                <ExpandableSection header="pom.xml">
                  <Box variant="pre">
                    {`<groupId>com.company</groupId>
<artifactId>${microservice.name}</artifactId>
<version>0.0.1-SNAPSHOT</version>`}
                  </Box>
                </ExpandableSection>
              </SpaceBetween>
            )
          }
        ]}
      />
    </Container>
  );
};

export default MicroserviceDetails;
